/**
 * UAPC Anytime - Search Page
 * @module SearchPage
 * @author Saif Ali Khan - Full Stack Developer
 * @description Parts search with AI recommendations
 * @version 1.0.0
 * Git: Saif Ali Khan <saif@linesoft.com> - Search functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Grid, List, Heart, ShoppingCart, X, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import Layout from '../components/Layout';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { user, token } = useAuth();
  const [results, setResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [categories, setCategories] = useState([]);
  const [makes, setMakes] = useState([]);
  
  const [filters, setFilters] = useState({
    query: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    make: searchParams.get('make') || '',
    model: searchParams.get('model') || '',
    year: searchParams.get('year') || '',
    condition: searchParams.get('condition') || '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchMakes();
  }, []);

  const performSearch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/search`, {
        query: filters.query,
        category: filters.category && filters.category !== 'all' ? filters.category : null,
        make: filters.make && filters.make !== 'all' ? filters.make : null,
        model: filters.model || null,
        year: filters.year ? parseInt(filters.year) : null,
        condition: filters.condition && filters.condition !== 'all' ? filters.condition : null,
        min_price: filters.minPrice ? parseFloat(filters.minPrice) : null,
        max_price: filters.maxPrice ? parseFloat(filters.maxPrice) : null
      });
      setResults(response.data.results || []);
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.query || filters.category || filters.make) {
        performSearch();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, performSearch]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMakes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/makes`);
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Failed to fetch makes:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (filters.query) params.set('q', filters.query);
    if (filters.category) params.set('category', filters.category);
    if (filters.make) params.set('make', filters.make);
    setSearchParams(params);
    performSearch();
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      category: 'all',
      make: 'all',
      model: '',
      year: '',
      condition: 'all',
      minPrice: '',
      maxPrice: ''
    });
    setSearchParams({});
  };

  const handleAddToCart = async (partId) => {
    await addToCart(partId);
  };

  const handleAddToFavorites = async (partId) => {
    if (!token) {
      toast.error('Please login to add favorites');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/favorites/${partId}`);
      toast.success('Added to favorites');
    } catch (error) {
      toast.error('Failed to add to favorites');
    }
  };

  const FiltersContent = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Category
        </label>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger data-testid="filter-category">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Vehicle Make
        </label>
        <Select value={filters.make} onValueChange={(v) => setFilters({ ...filters, make: v })}>
          <SelectTrigger data-testid="filter-make">
            <SelectValue placeholder="All Makes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Makes</SelectItem>
            {makes.map((make) => (
              <SelectItem key={make} value={make}>{make}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Model
        </label>
        <Input
          placeholder="e.g., Camry, F-150"
          value={filters.model}
          onChange={(e) => setFilters({ ...filters, model: e.target.value })}
          className="bg-slate-50 dark:bg-slate-800"
          data-testid="filter-model"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Year
        </label>
        <Input
          type="number"
          placeholder="e.g., 2020"
          value={filters.year}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
          className="bg-slate-50 dark:bg-slate-800"
          data-testid="filter-year"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Condition
        </label>
        <Select value={filters.condition} onValueChange={(v) => setFilters({ ...filters, condition: v })}>
          <SelectTrigger data-testid="filter-condition">
            <SelectValue placeholder="Any Condition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Condition</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="used">Used</SelectItem>
            <SelectItem value="refurbished">Refurbished</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Min Price
          </label>
          <Input
            type="number"
            placeholder="$0"
            value={filters.minPrice}
            onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
            className="bg-slate-50 dark:bg-slate-800"
            data-testid="filter-min-price"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Max Price
          </label>
          <Input
            type="number"
            placeholder="$9999"
            value={filters.maxPrice}
            onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
            className="bg-slate-50 dark:bg-slate-800"
            data-testid="filter-max-price"
          />
        </div>
      </div>

      <Button onClick={clearFilters} variant="outline" className="w-full" data-testid="clear-filters">
        <X className="w-4 h-4 mr-2" />
        Clear Filters
      </Button>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="search-page">
        {/* Search Header */}
        <div className="bg-slate-900 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <form onSubmit={handleSearch} className="flex gap-0">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by part number, name, or vehicle..."
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  className="h-14 pl-12 bg-white border-0 text-slate-900 placeholder:text-slate-500 text-lg"
                  data-testid="search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-14 px-8 bg-orange-500 hover:bg-orange-600 font-heading font-bold uppercase tracking-wider"
                data-testid="search-btn"
              >
                Search
              </Button>
            </form>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* AI Recommendations */}
          {recommendations.length > 0 && (
            <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-5 h-5 text-blue-600" />
                <span className="font-heading font-bold text-blue-900 dark:text-blue-100 uppercase text-sm">
                  AI Suggestions
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recommendations.map((rec, idx) => (
                  <button
                    key={idx}
                    onClick={() => setFilters({ ...filters, query: rec })}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                    data-testid={`suggestion-${idx}`}
                  >
                    {rec}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-8">
            {/* Desktop Filters Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-white dark:bg-slate-900 p-6 border border-slate-200 dark:border-slate-800 sticky top-24">
                <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white uppercase mb-6">
                  Filters
                </h3>
                <FiltersContent />
              </div>
            </aside>

            {/* Results */}
            <main className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">
                    {loading ? 'Searching...' : `${results.length} Results`}
                  </h2>
                  
                  {/* Mobile Filter Button */}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden" data-testid="mobile-filter-btn">
                        <Filter className="w-4 h-4 mr-2" />
                        Filters
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left">
                      <SheetHeader>
                        <SheetTitle>Filters</SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">
                        <FiltersContent />
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'text-blue-600' : 'text-slate-400'}`}
                    data-testid="view-grid"
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'text-blue-600' : 'text-slate-400'}`}
                    data-testid="view-list"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
                      <div className="aspect-square bg-slate-200 dark:bg-slate-800 animate-pulse mb-4" />
                      <div className="h-4 bg-slate-200 dark:bg-slate-800 animate-pulse mb-2" />
                      <div className="h-6 bg-slate-200 dark:bg-slate-800 animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-16">
                  <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">
                    No Parts Found
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400">
                    Try adjusting your search or filters to find what you're looking for.
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                  {results.map((part, index) => (
                    <div
                      key={part.id}
                      className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 hover:border-blue-500 transition-colors animate-fade-in-up"
                      data-testid={`part-card-${index}`}
                    >
                      <Link to={`/parts/${part.id}`}>
                        <div className="aspect-square bg-slate-100 dark:bg-slate-800 mb-4 flex items-center justify-center overflow-hidden">
                          {part.image_url ? (
                            <img src={part.image_url} alt={part.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-slate-400 font-mono text-sm">{part.part_number}</span>
                          )}
                        </div>
                      </Link>
                      <div className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-1">
                        {part.part_number}
                      </div>
                      <Link to={`/parts/${part.id}`}>
                        <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white line-clamp-2 hover:text-blue-600">
                          {part.name}
                        </h3>
                      </Link>
                      {part.make && part.model && (
                        <p className="text-sm text-slate-500 mt-1">
                          {part.make} {part.model} {part.year_from && `(${part.year_from}-${part.year_to || 'present'})`}
                        </p>
                      )}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-heading font-bold text-xl text-blue-600">
                          ${part.price.toFixed(2)}
                        </span>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          part.condition === 'new' ? 'bg-green-100 text-green-800' :
                          part.condition === 'refurbished' ? 'bg-blue-100 text-blue-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {part.condition}
                        </span>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Button 
                          onClick={() => handleAddToCart(part.id)}
                          className="flex-1 bg-orange-500 hover:bg-orange-600 text-sm"
                          data-testid={`add-to-cart-${index}`}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => handleAddToFavorites(part.id)}
                          data-testid={`add-to-favorites-${index}`}
                        >
                          <Heart className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {results.map((part, index) => (
                    <div
                      key={part.id}
                      className="flex gap-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 hover:border-blue-500 transition-colors"
                      data-testid={`part-list-${index}`}
                    >
                      <Link to={`/parts/${part.id}`} className="w-32 h-32 flex-shrink-0">
                        <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                          {part.image_url ? (
                            <img src={part.image_url} alt={part.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-slate-400 font-mono text-xs">{part.part_number}</span>
                          )}
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-slate-500 uppercase tracking-wider">
                          {part.part_number}
                        </div>
                        <Link to={`/parts/${part.id}`}>
                          <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white hover:text-blue-600">
                            {part.name}
                          </h3>
                        </Link>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                          {part.description}
                        </p>
                        {part.make && (
                          <p className="text-sm text-slate-500 mt-1">
                            Fits: {part.make} {part.model} {part.year_from && `(${part.year_from}-${part.year_to || 'present'})`}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <div className="text-right">
                          <div className="font-heading font-bold text-2xl text-blue-600">
                            ${part.price.toFixed(2)}
                          </div>
                          <span className={`inline-block px-2 py-1 text-xs font-bold uppercase ${
                            part.condition === 'new' ? 'bg-green-100 text-green-800' :
                            part.condition === 'refurbished' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {part.condition}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => handleAddToCart(part.id)}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add to Cart
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => handleAddToFavorites(part.id)}
                          >
                            <Heart className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SearchPage;
